import type { ApiResponse, ApiSpecialPage } from '../../../internalTypes';
import type { SpecialPage, SurveyPhaseFlags } from '../../../types/survey';
import { mapDisplayLogic } from '../question/shared/mapDisplayLogic';
import { mapSpecialPageCtas } from './mapCtaButton';

function resolveSpecialPageText(
  page: ApiSpecialPage | undefined,
  language?: string
): SpecialPage | undefined {
  if (!page?.id || page.disabled) return undefined;

  const translations = page.translations?.[language ?? ''];
  const header =
    translations?.headerRichText ||
    translations?.header ||
    page.headerRichText ||
    page.header ||
    page.title ||
    '';

  const description =
    translations?.descriptionRichText ||
    translations?.description ||
    page.descriptionRichText ||
    page.description;

  const ctas = mapSpecialPageCtas(page.ctaConfig, language ?? '');
  const ctaLabel = ctas?.start?.label;

  if (!header.trim()) return undefined;

  const displayLogic = mapDisplayLogic(page.controlConfig?.displayLogic);
  const title = translations?.title || page.title;

  return {
    id: page.id,
    header,
    ...(title ? { title } : {}),
    ...(description ? { description } : {}),
    ...(ctaLabel ? { ctaLabel } : {}),
    ...(ctas ? { ctas } : {}),
    ...(page.disabled !== undefined ? { disabled: page.disabled } : {}),
    ...(displayLogic ? { displayLogic } : {}),
  };
}

export function mapSpecialPages(api: ApiResponse, language?: string): {
  introPage?: SpecialPage;
  endPages: SpecialPage[];
  pausedPage?: SpecialPage;
  submittedPage?: SpecialPage;
  expiredPage?: SpecialPage;
  quotaFulfilledPage?: SpecialPage;
} {
  const dto = api.cfmSurveyDTO;
  if (!dto) {
    return { endPages: [] };
  }

  const endPages = (dto.endPages ?? [])
    .map(page => resolveSpecialPageText(page, language))
    .filter((page): page is SpecialPage => Boolean(page));

  return {
    introPage: resolveSpecialPageText(dto.introPage, language),
    endPages,
    pausedPage: resolveSpecialPageText(dto.pausedPage, language),
    submittedPage: resolveSpecialPageText(dto.submittedPage, language),
    expiredPage: resolveSpecialPageText(dto.expiredPage, language),
    quotaFulfilledPage: resolveSpecialPageText(dto.quotaFulfilledPage, language),
  };
}

export function mapPhaseFlags(api: ApiResponse): SurveyPhaseFlags {
  const dto = api.cfmSurveyDTO;
  const statusPaused = dto?.status === 'PAUSED';

  return {
    expired: Boolean(api.expired),
    alreadyFilled: Boolean(api.alreadyFilled || api.surveySubmitted),
    quotaFulfilled: Boolean(api.quotaFulfilled),
    paused: statusPaused,
  };
}

export function resolveInitialPhase(
  survey: {
    introPage?: SpecialPage;
    phaseFlags: SurveyPhaseFlags;
  }
): import('../../../types/survey').SurveyPhase {
  const { phaseFlags } = survey;

  if (phaseFlags.expired) return { kind: 'expired' };
  if (phaseFlags.quotaFulfilled) return { kind: 'quota_fulfilled' };
  if (phaseFlags.alreadyFilled) return { kind: 'submitted' };
  if (phaseFlags.paused) return { kind: 'paused' };
  if (survey.introPage) return { kind: 'intro' };
  return { kind: 'content', pageIndex: 0 };
}

/** True when fetch flags / intro config mean the respondent must not enter content navigation. */
export function isNonContentInitialSurvey(survey: {
  introPage?: SpecialPage;
  phaseFlags: SurveyPhaseFlags;
}): boolean {
  return resolveInitialPhase(survey).kind !== 'content';
}
